import type { OfflineSyncPdfPuntosEntregaPort, OfflineSyncRedencionExportPort } from '../../domain/ports/offlineSyncExportPorts.ts';
import type { PuntoEntregaDetail } from '@src/modules/puntos_entrega/domain/puntoEntregaModel.ts';
import { buildPuntoEntregaReportPdfFromShell } from '@src/modules/puntos_entrega/infrastructure/pdf/puntoEntregaReportPdfFromShell.ts';
import { collectRedencionBonoReportPdfInput } from '@src/modules/redencion_bono/infrastructure/pdf/redencionBonoReportPdfCollect.ts';
import { createReportPdfFactory } from '@src/modules/shared/report-pdf/factories/createReportPdfFactory.ts';
import type { OfflineSyncRedencionBundle } from '../../domain/offlineSyncPayload.ts';
import type { SyncPdfResolver } from '../../domain/ports/SyncPdfResolver.ts';

async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

export type CreateSyncPdfResolverDeps = {
  puntosEntregaShell: OfflineSyncPdfPuntosEntregaPort;
  redencionBonoApplication: Pick<OfflineSyncRedencionExportPort, 'getCatalog'>;
};

/** Builds the PDF resolver used when assembling offline sync wire payloads (data URLs, blob URLs, or regenerated PDFs). */
export function createSyncPdfResolver(deps: CreateSyncPdfResolverDeps): SyncPdfResolver {
  const pdfFactory = createReportPdfFactory();
  const { puntosEntregaShell, redencionBonoApplication } = deps;

  return async (type, payload) => {
    if (type === 'puntoEntrega') {
      const detail = payload as PuntoEntregaDetail;
      if (detail.informePdf.startsWith('data:')) return detail.informePdf;
      if (detail.informePdf.startsWith('blob:')) {
        try {
          const resp = await fetch(detail.informePdf);
          const blob = await resp.blob();
          return await blobToBase64(blob);
        } catch (e) {
          console.warn('Failed to fetch blob URL for puntoEntrega PDF, generating new one...', e);
        }
      }
      const blob = await buildPuntoEntregaReportPdfFromShell(puntosEntregaShell, detail);
      return await blobToBase64(blob);
    }

    const bundle = payload as OfflineSyncRedencionBundle;
    const header = bundle.header;
    if (header.informePdf && header.informePdf.startsWith('data:')) return header.informePdf;
    if (header.informePdf && header.informePdf.startsWith('blob:')) {
      try {
        const resp = await fetch(header.informePdf);
        const blob = await resp.blob();
        return await blobToBase64(blob);
      } catch (e) {
        console.warn('Failed to fetch blob URL for redencionBono PDF, generating new one...', e);
      }
    }
    const collected = collectRedencionBonoReportPdfInput({
      publicId: header.publicId,
      header: {
        entregaId: header.entregaId,
        esRemanente: header.esRemanente,
        fecha: header.fecha,
        proveedorId: header.proveedorId,
        responsableId: header.responsableId,
        responsableNombre: header.responsableNombre,
        latitud: header.latitud ?? '',
        longitud: header.longitud ?? '',
      },
      participantes: bundle.participantes,
      catalog: redencionBonoApplication.getCatalog(),
      barrioComunaLookup: puntosEntregaShell,
      findParticipante: (pid) => {
        const p = puntosEntregaShell.findParticipanteById(pid);
        if (!p) return null;
        return { documento: p.documento, nombre: p.nombre, barrioId: p.barrioId };
      },
    });
    if (!collected.ok) return '';
    const blob = await pdfFactory.buildRedencionBonoFo1775(collected.input);
    return await blobToBase64(blob);
  };
}
